// src/pages/CreateOrder.js

import React, { useState, useEffect } from 'react'; // Import useEffect
import { db } from '../firebase';
import { collection, serverTimestamp, doc, setDoc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function CreateOrder() {
  const { userProfile, currentUser } = useAuth();
  const navigate = useNavigate();

  const [orderNumber, setOrderNumber] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  // REMOVED: const [deliveryDate, setDeliveryDate] = useState(''); // This will now be calculated
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // --- NUEVOS ESTADOS PARA LA LÓGICA DE FECHAS ---
  const [orderCreationDate, setOrderCreationDate] = useState(''); // Fecha de Creación (la de hoy)
  const [deliveryDays, setDeliveryDays] = useState(''); // Días de Entrega (input numérico)
  const [calculatedExpectedDeliveryDate, setCalculatedExpectedDeliveryDate] = useState(''); // Fecha Prevista Calculada
  // --- FIN NUEVOS ESTADOS ---

  const orderStatuses = [
    'En proceso de Compra',
    'En almacén',
    'Programado para despacho',
    'En Ruta',
    'Reprogramado',
    'Entregado Exitosamente',
    'Pedido listo para Recojo',
    'Entregado en Agencia'
  ];
  const defaultStatus = 'En proceso de Compra';

  const ordersCollectionRef = collection(db, 'pedidos');

  // --- useEffect para inicializar orderCreationDate a la fecha actual ---
  useEffect(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    setOrderCreationDate(`${year}-${month}-${day}`);
  }, []);

  // --- useEffect para calcular calculatedExpectedDeliveryDate ---
  useEffect(() => {
    if (orderCreationDate && deliveryDays !== '' && !isNaN(parseInt(deliveryDays))) {
      const baseDate = new Date(orderCreationDate);
      baseDate.setDate(baseDate.getDate() + parseInt(deliveryDays)); // Sumar los días
      
      const year = baseDate.getFullYear();
      const month = String(baseDate.getMonth() + 1).padStart(2, '0');
      const day = String(baseDate.getDate()).padStart(2, '0');
      setCalculatedExpectedDeliveryDate(`${year}-${month}-${day}`);
    } else {
      setCalculatedExpectedDeliveryDate(''); // Limpiar si los inputs no son válidos
    }
  }, [orderCreationDate, deliveryDays]);
  // --- FIN LÓGICA DE FECHAS ---


  const handleCreateOrder = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validaciones de campos (actualizadas)
    if (!orderNumber || !clientName || !clientEmail || !contactName || !contactPhone || !deliveryAddress || deliveryDays === '' || isNaN(parseInt(deliveryDays)) || parseInt(deliveryDays) < 0) {
      setError('Todos los campos son obligatorios y los días de entrega deben ser un número positivo.');
      return;
    }
    if (!/^\d{5}$/.test(orderNumber)) {
      setError('El número de pedido debe ser de 5 dígitos numéricos.');
      return;
    }
    if (!/^[\w.-]+@([\w-]+\.)+[\w-]{2,4}$/.test(clientEmail)) {
        setError('El correo electrónico del cliente no es válido.');
        return;
    }
    if (!/^\d{9}$/.test(contactPhone)) {
        setError('El teléfono de contacto debe ser de 9 dígitos numéricos.');
        return;
    }

    try {
      // Convertir a mayúsculas antes de la verificación y guardado
      const formattedOrderNumber = orderNumber.toUpperCase();
      const formattedClientName = clientName.toUpperCase();
      const formattedContactName = contactName.toUpperCase();
      const formattedDeliveryAddress = deliveryAddress.toUpperCase();

      // Verificar si el número de pedido ya existe
      const orderDocRef = doc(ordersCollectionRef, formattedOrderNumber);
      const orderDocSnap = await getDoc(orderDocRef);
      if (orderDocSnap.exists()) {
        setError(`El número de pedido ${formattedOrderNumber} ya existe. Por favor, usa otro.`);
        return;
      }

      // Crear el pedido usando setDoc con el orderNumber como ID
      await setDoc(orderDocRef, {
        orderDate: orderCreationDate, // Fecha de creación (la de hoy)
        orderNumber: formattedOrderNumber,
        clientName: formattedClientName,
        clientEmail: clientEmail.toLowerCase(),
        contactName: formattedContactName,
        contactPhone: contactPhone,
        deliveryAddress: formattedDeliveryAddress,
        currentStatus: defaultStatus,
        deliveryDays: parseInt(deliveryDays), // Guardar los días de entrega
        expectedDeliveryDate: calculatedExpectedDeliveryDate, // Guardar la fecha prevista calculada
        createdAt: serverTimestamp(),
        createdBy: currentUser.email,
        createdByUid: currentUser.uid,
        driverId: null,
        vehicleId: null,
        observations: null,
        dispatchType: null,
        agencyTrackingNumber: null,
        agencyName: null,
        agencyDestination: null,
        agencyKey: null,
        statusHistory: [{
            status: defaultStatus,
            timestamp: new Date().toISOString(), // Usar new Date().toISOString() para el historial
            user: currentUser.email
        }]
      });

      setSuccess('Pedido creado exitosamente!');
      // Limpiar formulario
      setOrderNumber('');
      setClientName('');
      setClientEmail('');
      setContactName('');
      setContactPhone('');
      setDeliveryDays(''); // Limpiar días de entrega
      setDeliveryAddress('');
      // orderCreationDate se reinicializará a la fecha actual automáticamente por el useEffect
      // calculatedExpectedDeliveryDate se limpiará si deliveryDays se vacía
      
    } catch (err) {
      console.error("Error al crear pedido:", err.message);
      setError("No se pudo crear el pedido: " + err.message);
    }
  };

  const handleCancel = () => {
    setOrderNumber('');
    setClientName('');
    setClientEmail('');
    setContactName('');
    setContactPhone('');
    setDeliveryDays(''); // Limpiar días de entrega
    setDeliveryAddress('');
    setError('');
    setSuccess('');
    navigate('/my-orders'); // Redirigir a MyOrders o a donde desees después de cancelar
  };

  const canCreateOrders = userProfile && (
    userProfile.rol === 'Administrador' ||
    userProfile.rol === 'Atención al Cliente' ||
    userProfile.rol === 'Asesor'
  );

  if (!canCreateOrders) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', color: 'red' }} className="app-content-container">
        <h3>Acceso Denegado</h3>
        <p>No tienes los permisos necesarios para crear pedidos.</p>
      </div>
    );
  }

  // const today = new Date().toISOString().split('T')[0]; // Ya no se necesita aquí, se maneja en el estado orderCreationDate

  return (
    <div className="app-content-container">
      <h2 style={{ textAlign: 'center' }}>Crear Nuevo Pedido</h2>

      {error && <p className="login-error">{error}</p>}
      {success && <p style={{ color: 'green', backgroundColor: '#e6ffe6', border: '1px solid green', padding: '10px', borderRadius: '5px', marginBottom: '20px', textAlign: 'center' }}>{success}</p>}

      <form onSubmit={handleCreateOrder} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
        <div className="login-form-group">
          <label htmlFor="orderCreationDate">Fecha de Pedido:</label>
          <input
            type="date"
            id="orderCreationDate"
            value={orderCreationDate} // Usar el estado orderCreationDate
            disabled
            style={{ backgroundColor: '#f0f0f0' }}
          />
        </div>

        <div className="login-form-group">
          <label htmlFor="orderNumber">Número de Pedido (5 dígitos):</label>
          <input
            type="text"
            id="orderNumber"
            placeholder="Ej: 12345"
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value.toUpperCase())}
            maxLength={5}
            pattern="\d{5}"
            title="Debe ser un número de 5 dígitos"
            required
          />
        </div>

        <div className="login-form-group">
          <label htmlFor="clientName">Nombre del Cliente:</label>
          <input
            type="text"
            id="clientName"
            placeholder="Nombre Completo del Cliente"
            value={clientName}
            onChange={(e) => setClientName(e.target.value.toUpperCase())}
            required
          />
        </div>

        <div className="login-form-group">
          <label htmlFor="clientEmail">Correo Electrónico del Cliente:</label>
          <input
            type="email"
            id="clientEmail"
            placeholder="cliente@ejemplo.com"
            value={clientEmail}
            onChange={(e) => setClientEmail(e.target.value)}
            required
          />
        </div>

        <div className="login-form-group">
          <label htmlFor="contactName">Nombre del Contacto:</label>
          <input
            type="text"
            id="contactName"
            placeholder="Nombre del Contacto para la entrega"
            value={contactName}
            onChange={(e) => setContactName(e.target.value.toUpperCase())}
            required
          />
        </div>

        <div className="login-form-group">
          <label htmlFor="contactPhone">Teléfono del Contacto (9 dígitos):</label>
          <input
            type="tel"
            id="contactPhone"
            placeholder="Ej: 987654321"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            maxLength={9}
            pattern="\d{9}"
            title="Debe ser un número de 9 dígitos"
            required
          />
        </div>

        <div className="login-form-group">
          <label htmlFor="deliveryAddress">Dirección de Entrega:</label>
          <input
            type="text"
            id="deliveryAddress"
            placeholder="Dirección completa de entrega"
            value={deliveryAddress}
            onChange={(e) => setDeliveryAddress(e.target.value.toUpperCase())}
            required
          />
        </div>

        <div className="login-form-group">
          <label htmlFor="currentStatus">Estado Actual:</label>
          <select
            id="currentStatus"
            value={defaultStatus}
            disabled
            style={{ backgroundColor: '#f0f0f0' }}
          >
            {orderStatuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>

        {/* --- NUEVOS CAMPOS DE FECHA --- */}
        <div className="login-form-group">
          <label htmlFor="deliveryDays">Días de Entrega:</label>
          <input
            type="number"
            id="deliveryDays"
            value={deliveryDays}
            onChange={(e) => setDeliveryDays(e.target.value)}
            min="0" // No permitir días negativos
            required
          />
        </div>

        <div className="login-form-group">
          <label htmlFor="calculatedExpectedDeliveryDate">Fecha Prevista de Entrega:</label>
          <input
            type="date"
            id="calculatedExpectedDeliveryDate"
            value={calculatedExpectedDeliveryDate}
            disabled // Deshabilitado porque se calcula
            style={{ backgroundColor: '#f0f0f0' }}
          />
        </div>
        {/* --- FIN NUEVOS CAMPOS DE FECHA --- */}

        <div style={{ display: 'flex', gap: '20px', marginTop: '10px', justifyContent: 'center' }}>
          <button
            type="submit"
            style={{
              backgroundColor: 'var(--secondary-blue)',
              color: 'white',
              flex: 1,
              height: '35px',
              maxWidth: '200px',
              fontWeight: 'bold',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color 0.3s ease, transform 0.2s ease',
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            Crear Pedido
          </button>
          <button
            type="button"
            onClick={handleCancel}
            style={{
              backgroundColor: 'var(--primary-red)',
              color: 'white',
              flex: 1,
              height: '35px',
              maxWidth: '200px',
              fontWeight: 'bold',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color 0.3s ease, transform 0.2s ease',
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreateOrder;